<template>
  <div
    :class="$style.commands"
  >
    <div :class="$style.sectionHeader">
      Security Commands
      <IconTooltip :tooltipContainerClass="$style.tooltip">
        <p>
          {{ getTooltipInfo }}
        </p>
        <a
          :href="gettingStartedLink"
          rel="noopener noreferrer"
          target="_blank"
        >{{ getLearnMoreAbout }}</a>
      </IconTooltip>
    </div>
    <div
      :class="$style.sectionHeaderText"
    >
      <div v-if="isEnrolledInMdm">
        <a
          :href="mdmCommandsLink"
          rel="noopener noreferrer"
          target="_blank"
        >Learn more</a> about when
        to use the following commands.
      </div>
      <div v-if="!isEnrolledInMdm">
        <a
          :href="gettingStartedLink"
          rel="noopener noreferrer"
          target="_blank"
        >{{ getEnrollCommandsInfo.value }}</a>
        {{ getEnrollCommandsInfo.text }}
      </div>
    </div>
    <ul :class="$style.sectionContent">
      <li :class="$style.sectionContentItem">
        <FlatActionButton
          :action="handleLockMDM"
          :buttonClass="['btnDefault', 'btnSmallWidth']"
          :class="$style.commandButton"
          :disabled="isLockDisabled"
          :text="`Lock ${deviceItem.label}`"
        />
      </li>
      <li
        v-if="!isIOS && !isAndroid"
        :class="$style.sectionContentItem"
      >
        <FlatActionButton
          :action="handleRestartMDM"
          :buttonClass="['btnDefault', 'btnSmallWidth']"
          :class="$style.commandButton"
          :disabled="isRestartDisabled"
          :text="`Restart ${deviceItem.label}`"
        />
      </li>
      <li
        v-if="!isIOS && !isAndroid"
        :class="$style.sectionContentItem"
      >
        <FlatActionButton
          :action="handleShutdownMDM"
          :buttonClass="['btnDefault', 'btnSmallWidth']"
          :class="$style.commandButton"
          :disabled="isShutdownDisabled"
          :text="`Shut Down ${deviceItem.label}`"
        />
      </li>
      <li
        v-if="isAndroid"
        :class="$style.sectionContentItem"
      >
        <FlatActionButton
          :action="handleResetPasscodeMDM"
          :buttonClass="['btnDefault', 'btnSmallWidth']"
          :class="$style.commandButton"
          data-test-id="SystemCommands_handleResetPasscodeMDM"
          :disabled="isResetPasscodeDisabled"
          :text="`Reset Passcode`"
        />
      </li>
      <li
        v-if="isIOS && isUserEnrolledInMDM"
        :class="$style.sectionContentItem"
      >
        <FlatActionButton
          :action="handleUnenrollIosFromMDM"
          :buttonClass="['btnDefault', 'btnSmallWidth', 'btnAlert']"
          :class="$style.commandButton"
          data-test-id="SystemCommands_handleUnenrollIosFromMDM"
          :disabled="isEraseDisabled"
          :text="`Unenroll ${deviceItem.label}`"
        />
      </li>
      <li
        v-else
        :class="$style.sectionContentItem"
      >
        <FlatActionButton
          :action="handleEraseMDM"
          :buttonClass="['btnDefault', 'btnSmallWidth', 'btnAlert']"
          :class="$style.commandButton"
          data-test-id="SystemCommands_handleEraseMDM"
          :disabled="isEraseDisabled"
          :text="`Erase ${deviceItem.label}`"
        />
      </li>
    </ul>
  </div>
</template>
<script>
import {
  ItemTypeConsts,
  StoreModelMap,
  SystemCommandsConsts,
} from 'util/Constants';
import { isEmpty } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import FlatActionButton from 'components/Buttons/FlatActionButton';
import GoogleMdmApi from 'client/api/v2/GoogleMdmApi';
import IconTooltip from 'components/Tooltip/IconTooltip';
import ItemType from 'client/util/Constants/ItemType';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import MdmsApi from 'client/api/v2/MdmsApi';
import ModalEventBus from 'components/Modal/ModalEventBus';
import SystemApi from 'client/api/v2/SystemApi';
import SystemEmmCommandsModal from 'client/systems/SystemEmmCommandsModal';
import SystemEmmCommandsPasscodeModal from 'client/systems/SystemEmmCommandsPasscodeModal';
import SystemMdmCommandsModal from 'client/systems/SystemMdmCommandsModal';
import SystemMdmCommandsPinModal from 'client/systems/SystemMdmCommandsPinModal.vue';

const { system } = ItemTypeConsts;
const systemModel = StoreModelMap[system];
const { DeviceItemType } = ItemType;

export default {
  name: 'SystemCommands',

  components: {
    IconTooltip,
    FlatActionButton,
  },

  data: () => ({
    deviceItem: new DeviceItemType(),
    eraseExecuted: false,
    lockExecuted: false,
    restartExecuted: false,
    shutdownExecuted: false,
    resetPasscodeExecuted: false,
    mdmCommandsLink: KbArticleUrls.mdmCommands,
    mdmGettingStartedLink: KbArticleUrls.gettingStartedMdm,
    securityCommandsGettingStartedLink: KbArticleUrls.gettingStartedSecurityCommands,
    systemCommandsBodyText: SystemCommandsConsts.systemCommandsBody,
  }),

  computed: {
    ...mapGetters(systemModel, [
      'getBuiltInCommands',
      'getEnrollmentType',
      'getEmmDeviceId',
      'getMdmDevices',
      'getSystemMdmId',
      'id',
      'isIOS',
      'isLinux',
      'isMacOS',
      'isOrgMdmEnrolled',
      'isSystemMdmEnrolled',
      'isWindows',
      'isAndroid',
    ]),

    getDmId() {
      const mdmDevices = this.getMdmDevices;
      if (isEmpty(mdmDevices)) return '';

      return mdmDevices[0].id;
    },

    getEnrollCommandsInfo() {
      if (this.isMacOS) {
        return {
          value: `Enroll this ${this.deviceItem.label}`,
          text: 'to enable Apple\'s MDM commands below.',
        };
      }
      return {
        value: 'Learn More',
        text: 'about when to use the following commands.',
      };
    },

    getLearnMoreAbout() {
      if (this.isMacOS || this.isIOS || this.isAndroid) {
        return 'Learn more about MDM';
      }
      return 'Learn more';
    },

    getTooltipInfo() {
      let osCommands;
      if (this.isMacOS || this.isIOS) {
        osCommands = "Apple's MDM";
      } else if (this.isWindows) {
        osCommands = 'Windows';
      } else if (this.isAndroid) {
        osCommands = 'Android';
      } else {
        osCommands = 'Linux';
      }
      return `Use ${osCommands} commands to gain more control over your ${this.deviceItem.labelPlural}.`;
    },

    gettingStartedLink() {
      if (this.isMacOS || this.isIOS || this.isAndroid) {
        return this.mdmGettingStartedLink;
      }
      return this.securityCommandsGettingStartedLink;
    },

    isEnrolledInMdm() {
      return this.isOrgMdmEnrolled && this.isSystemMdmEnrolled;
    },

    isEraseDisabled() {
      if (this.isLinux || this.isAndroid) {
        return this.eraseExecuted;
      }
      if (this.isMacOS || this.isIOS) {
        return !this.isEnrolledInMdm || this.eraseExecuted;
      }
      return !this.isBuiltInCommand('security', 'erase') || this.eraseExecuted;
    },

    isLockDisabled() {
      if (this.isLinux || this.isAndroid) {
        return this.lockExecuted;
      }
      if (this.isMacOS || this.isIOS) {
        return !this.isEnrolledInMdm || this.lockExecuted;
      }
      return !this.isBuiltInCommand('security', 'lock') || this.lockExecuted;
    },

    isResetPasscodeDisabled() {
      return this.resetPasscodeExecuted;
    },

    isRestartDisabled() {
      if (this.isLinux) {
        return this.restartExecuted;
      }
      if (this.isMacOS || this.isIOS) {
        return !this.isEnrolledInMdm || this.restartExecuted;
      }
      return !this.isBuiltInCommand('security', 'restart') || this.restartExecuted;
    },

    isShutdownDisabled() {
      if (this.isLinux) {
        return this.shutdownExecuted;
      }
      if (this.isMacOS || this.isIOS) {
        return !this.isEnrolledInMdm || this.shutdownExecuted;
      }
      return !this.isBuiltInCommand('security', 'shutdown') || this.shutdownExecuted;
    },

    isUserEnrolledInMDM() {
      return this.getEnrollmentType === 'user';
    },
  },

  mounted() {
    this.fetchMdmSettings();
    ModalEventBus.$on('closeErase', this.disableEraseButton);
    ModalEventBus.$on('closeLock', this.disableLockButton);
    ModalEventBus.$on('closeRestart', this.disableRestartButton);
    ModalEventBus.$on('closeShutdown', this.disableShutdownButton);
    ModalEventBus.$on('closeResetPasscode', this.disableResetPasscodeButton);
    this.deviceItem.label = this.deviceItem.label.charAt(0).toUpperCase()
      + this.deviceItem.label.slice(1);
  },

  methods: {
    ...mapActions(systemModel, [
      'fetchMdmSettings',
    ]),

    disableEraseButton() {
      this.eraseExecuted = true;
    },

    disableLockButton() {
      this.lockExecuted = true;
    },

    disableResetPasscodeButton() {
      this.resetPasscodeExecuted = true;
    },

    disableRestartButton() {
      this.restartExecuted = true;
    },

    disableShutdownButton() {
      this.shutdownExecuted = true;
    },

    executeErase() {
      if (this.isIOS) {
        return MdmsApi.postEraseDevice(this.getDmId, this.getSystemMdmId, { pin: '000000' });
      }

      if (this.isAndroid) {
        return GoogleMdmApi.postEraseDevice(this.getEmmDeviceId);
      }

      return SystemApi.postEraseDevice(this.id, { type: 'security' });
    },

    executeLock() {
      if (this.isIOS) {
        return MdmsApi.postLockDevice(this.getDmId, this.getSystemMdmId, { pin: '000000' });
      }

      if (this.isAndroid) {
        return GoogleMdmApi.postLockDevice(this.getEmmDeviceId);
      }

      return SystemApi.postLockDevice(this.id, { type: 'security' });
    },

    executeResetPasscode(deviceId, payload) {
      return GoogleMdmApi.postResetPasscode(
        deviceId,
        payload,
      );
    },

    executeRestart() {
      if (this.isMacOS || this.isIOS) {
        return MdmsApi.postRestartDevice(this.getDmId, this.getSystemMdmId);
      }
      return SystemApi.postRestartDevice(this.id, { type: 'security' });
    },

    executeShutdown() {
      if (this.isMacOS || this.isIOS) {
        return MdmsApi.postShutdownDevice(this.getDmId, this.getSystemMdmId);
      }
      return SystemApi.postShutdownDevice(this.id, { type: 'security' });
    },

    executeUnenroll() {
      return MdmsApi.deleteMdmUserEnrollment(this.getDmId, this.getSystemMdmId);
    },

    handleEraseMDM() {
      let component;
      const componentProps = {
        actionEvent: 'closeErase',
        actionText: 'yes, erase',
        header: 'Erase This Device',
      };

      if (this.isMacOS) {
        component = SystemMdmCommandsPinModal;
        componentProps.bodyText = ['**Are you sure** you want to erase this device?', '**Everything on the hard drive including macOS software is removed** and you can\'t use this device until you reinstall the macOS software using the pin you create below. **Store this pin in a safe place**. It takes 5-10 minutes for this device\'s status to change.'];
        componentProps.dmId = this.getDmId;
        componentProps.mdmsApiAction = (...args) => MdmsApi.postEraseDevice(...args);
        componentProps.systemId = this.getSystemMdmId;
      } else if (this.isIOS) {
        const appleDocumentationUrl = 'https://support.apple.com/guide/deployment-reference-macos/remote-wipe-apd713df1b14/web';
        component = SystemMdmCommandsModal;
        componentProps.body = ['**Are you sure** you want to erase this iOS device?', `Erasing the device removes **all data from the device**, even if the device is locked. The user is not warned of this action. The user cannot access this device until you unlock it and complete setup. Learn more: [MDM Commands](${this.mdmCommandsLink}) and [Apple documentation](${appleDocumentationUrl}) on remote wipe.`];
        componentProps.executeAction = this.executeErase;
        componentProps.onSuccess = 'Erase security command sent to device.';
      } else if (this.isWindows) {
        component = SystemMdmCommandsModal;
        componentProps.body = ['**Are you sure you want to erase this device?**', '**Everything on the hard drive, including Windows software, is removed and can\'t be recovered.** The device will restart once Windows software is reinstalled.'];
        componentProps.executeAction = this.executeErase;
        componentProps.onSuccess = 'Erase security command sent to device.';
      } else if (this.isAndroid) {
        component = SystemEmmCommandsModal;
        componentProps.body = this.systemCommandsBodyText.androidEraseDeviceText;
        componentProps.executeAction = this.executeErase;
        componentProps.onSuccess = this.systemCommandsBodyText.androidEraseDeviceOnSuccess;
      } else {
        component = SystemMdmCommandsModal;
        componentProps.body = ['**Are you sure you want to erase this device?**', '**Everything on the hard drive, including Linux software, is removed and can\'t be recovered.** The device will restart once Linux software is reinstalled.'];
        componentProps.executeAction = this.executeErase;
        componentProps.onSuccess = 'Erase security command sent to device.';
      }

      ModalEventBus.open({ component, componentProps });
    },

    handleLockMDM() {
      let component;
      const componentProps = {
        actionEvent: 'closeLock',
        actionText: 'yes, lock',
        header: 'Lock This Device',
      };

      if (this.isIOS) {
        component = SystemMdmCommandsModal;
        componentProps.body = ['**Are you sure** you want to lock this lost iOS device?',
          `The device will remain locked until the user enters the iPhone passcode. Learn more: [MDM Commands](${this.mdmCommandsLink})`];
        componentProps.executeAction = this.executeLock;
        componentProps.onSuccess = 'Lock security command sent to device.';
      } else if (this.isMacOS) {
        component = SystemMdmCommandsPinModal;
        componentProps.bodyText = ['**Are you sure** you want to lock this device?', 'A user can\'t login into a locked device. You can unlock the device by using the numerical pin you create below. **Store this pin in a safe place** It takes 5-10 minutes for this device\'s status to change.'];
        componentProps.dmId = this.getDmId;
        componentProps.mdmsApiAction = (...args) => MdmsApi.postLockDevice(...args);
        componentProps.systemId = this.getSystemMdmId;
      } else if (this.isAndroid) {
        component = SystemEmmCommandsModal;
        componentProps.body = this.systemCommandsBodyText.androidLockDeviceText;
        componentProps.executeAction = this.executeLock;
        componentProps.onSuccess = this.systemCommandsBodyText.androidLockDeviceTextOnSuccess;
      } else {
        component = SystemMdmCommandsModal;
        componentProps.body = ['**Are you sure you want to lock this device?**', 'Any bound users will be able to unlock the device by logging in.'];
        componentProps.executeAction = this.executeLock;
        componentProps.header = 'Remotely Lock This Device';
        componentProps.onSuccess = 'Lock security command sent to device.';
      }

      ModalEventBus.open({ component, componentProps });
    },

    handleResetPasscodeMDM() {
      ModalEventBus.open({
        component: SystemEmmCommandsPasscodeModal,
        componentProps: {
          actionText: 'save',
          actionEvent: 'closeResetPasscode',
          body: ['Enter a new passcode in the field below. Passcode must be a minimum of 8 characters and at least one special character.'],
          emmApiAction: this.executeResetPasscode,
          header: 'Reset Passcode',
          inputLabel: 'Enter Passcode',
          onSuccess: 'Passcode Reset successfully!.',
          id: this.getEmmDeviceId,
        },
      });
    },

    handleRestartMDM() {
      ModalEventBus.open({
        component: SystemMdmCommandsModal,
        componentProps: {
          actionText: 'yes, restart',
          actionEvent: 'closeRestart',
          body: ['**Are you sure you want to restart this device?**',
            '**Any unsaved progress will be lost.** The JumpCloud agent may not detect quickly restarted devices, so the device\'s status may not change.'],
          executeAction: this.executeRestart,
          header: 'Restart This Device',
          onSuccess: 'Restart security command sent to device.',
        },
      });
    },

    handleShutdownMDM() {
      ModalEventBus.open({
        component: SystemMdmCommandsModal,
        componentProps: {
          actionText: 'yes, shut down',
          actionEvent: 'closeShutdown',
          body: ['**Are you sure you want to shut down this device?**', 'Any unsaved progress will be lost, and the device will be shut down.'],
          executeAction: this.executeShutdown,
          header: 'Shut Down This Device',
          onSuccess: 'Shut Down security command sent to device.',
        },
      });
    },

    handleUnenrollIosFromMDM() {
      const component = SystemMdmCommandsModal;
      const componentProps = {
        actionEvent: 'closeErase',
        actionText: 'yes, unenroll',
        header: 'Unenroll This Device',
        body: ['**Are you sure you want to remove this iOS device from MDM enrollment?**',
          `**This personal iOS device was enrolled by the user and will now be unenrolled from MDM.** The user is not warned of this action. Unenrolling a device does not remove the device from the Devices list. [Learn More](${this.mdmCommandsLink})`],
        executeAction: this.executeUnenroll,
        onSuccess: 'Unenroll command sent to device.',
      };
      ModalEventBus.open({ component, componentProps });
    },

    isBuiltInCommand(commandType, commandName) {
      return this.getBuiltInCommands.some(c => c.type === commandType
      && c.name === commandName);
    },
  },
};
</script>
<style module>
  @value (
    gradientDark2,
    gradientLight3,
    greyLight3
  ) from '../css/colors.css';

  .commands a:hover {
    cursor: pointer;
  }

  .commands {
    border-top: 1px solid greyLight3;
    margin-left: 20px;
    margin-right: 20px;
    margin-top: 20px;
    padding-top: 20px;
  }

  .sectionHeader {
    color: gradientDark2;
    font-size: 14px;
    font-weight: 700;
    margin: 0;
    position: relative;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sectionHeaderText {
    font-size: 12px;
  }

  .sectionContent {
    list-style-type: none;
    padding: 20px 0;
  }

  .sectionContentItem {
    margin: 5px 0 10px 0;
  }

  .commandButton {
    width: 100%;
  }

  .tooltip {
    margin-left: 5px;
    position: relative;
    top: 0;
  }
</style>
